import React, { useEffect, useRef, useState } from 'react';
import { Phone, PhoneOff, Video, VideoOff, Mic, MicOff } from 'lucide-react';
import { auth } from '../firebase-config';
import { vibrate } from '../utils/vibration';
import {
  CALL_EVENT, RING_TIMEOUT_MS,
  getLocalStream, startCall, answerCall, setCallStatus,
  watchCall, watchIncomingCalls, teardown,
} from '../services/callService';

/**
 * CallManager — the app-wide 1:1 video call overlay (Option A,
 * 2026-08-31). Mounted ONCE at layout level so an incoming call rings on
 * ANY panel, not just the chat. ChatV2's 📹 button dispatches CALL_EVENT;
 * everything else lives here: outgoing ring, incoming ring (with the
 * vibration pattern), the in-call view (remote full-bleed, local PiP,
 * mute / camera / hang-up), and teardown from EITHER side's status write.
 *
 * Phase 1 rings only while the app is open — ringing a closed app needs
 * the push notifier extended with call alerts (phase 2).
 */

const overlayStyle = {
  position: 'fixed', inset: 0, zIndex: 400,
  background: 'rgba(10,14,26,0.97)',
  display: 'flex', flexDirection: 'column',
  color: '#E8EAF0',
};

const roundBtn = (bg) => ({
  margin: 0, border: 'none', cursor: 'pointer',
  width: 60, height: 60, borderRadius: '50%',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  background: bg, color: '#fff', flexShrink: 0,
});

const CallManager = () => {
  const myUid = auth.currentUser?.uid || null;

  // mode: null | 'outgoing' | 'incoming' | 'active'
  const [mode, setMode] = useState(null);
  const [peerName, setPeerName] = useState('');
  const [note, setNote] = useState('');
  const [micOn, setMicOn] = useState(true);
  const [camOn, setCamOn] = useState(true);
  const [incoming, setIncoming] = useState(null);

  const sessionRef = useRef(null);   // { callId, pc, unsubs, localStream, watchUnsub, ringTimer }
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const ringBuzzRef = useRef(null);

  const attach = (ref, stream) => {
    if (ref.current && stream && ref.current.srcObject !== stream) {
      ref.current.srcObject = stream;
    }
  };

  const stopRingBuzz = () => { clearInterval(ringBuzzRef.current); ringBuzzRef.current = null; };

  const endSession = (status) => {
    const s = sessionRef.current;
    sessionRef.current = null;
    stopRingBuzz();
    if (s) {
      clearTimeout(s.ringTimer);
      try { s.watchUnsub?.(); } catch (_) {}
      if (status && s.callId) setCallStatus(s.callId, status);
      teardown(s);
    }
    setMode(null); setIncoming(null); setMicOn(true); setCamOn(true);
  };

  // Watch the live call doc so the OTHER side's decline/hang-up tears us down.
  const watchStatus = (callId) => watchCall(callId, (call) => {
    if (!call) return;
    if (call.status === 'accepted' && sessionRef.current?.role === 'caller') {
      clearTimeout(sessionRef.current?.ringTimer);
      setMode('active');
    }
    if (['declined', 'ended', 'missed'].includes(call.status)) {
      setNote(call.status === 'declined' ? 'Call declined.' : call.status === 'missed' ? 'No answer.' : 'Call ended.');
      setTimeout(() => setNote(''), 2500);
      endSession(null); // status already written by the other side
    }
  });

  // ── outgoing: ChatV2 (or anyone) asks for a call ──────────────────────
  useEffect(() => {
    const onRequest = async (ev) => {
      const d = ev?.detail || {};
      if (!d.calleeUid || sessionRef.current) return;
      try {
        const localStream = await getLocalStream();
        setPeerName(d.calleeName || 'Guest');
        setMode('outgoing');
        const handle = await startCall({
          chatId: d.chatId || null,
          calleeUid: d.calleeUid,
          calleeName: d.calleeName || 'Guest',
          callerName: auth.currentUser?.displayName || auth.currentUser?.email || 'Guest',
          localStream,
          onRemoteStream: (remote) => attach(remoteVideoRef, remote),
        });
        const ringTimer = setTimeout(() => {
          setNote('No answer.');
          setTimeout(() => setNote(''), 2500);
          endSession('missed');
        }, RING_TIMEOUT_MS);
        sessionRef.current = {
          ...handle, localStream, role: 'caller', ringTimer,
          watchUnsub: watchStatus(handle.callId),
        };
        attach(localVideoRef, localStream);
      } catch (e) {
        setNote(e?.message || 'Could not start the call — camera permission?');
        setTimeout(() => setNote(''), 4000);
        endSession(null);
      }
    };
    window.addEventListener(CALL_EVENT, onRequest);
    return () => window.removeEventListener(CALL_EVENT, onRequest);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── incoming: ring while the app is open ──────────────────────────────
  useEffect(() => {
    if (!myUid) return undefined;
    return watchIncomingCalls(myUid, (call) => {
      if (sessionRef.current) return;        // already on a call
      setIncoming(call);
      if (call) {
        setPeerName(call.callerName || 'Guest');
        setMode('incoming');
        if (!ringBuzzRef.current) {
          vibrate([500, 250, 500]);
          ringBuzzRef.current = setInterval(() => vibrate([500, 250, 500]), 2500);
        }
      } else {
        stopRingBuzz();
        setMode((m) => (m === 'incoming' ? null : m));
      }
    });
  }, [myUid]);

  const accept = async () => {
    const call = incoming;
    if (!call) return;
    stopRingBuzz();
    try {
      const localStream = await getLocalStream();
      const handle = await answerCall({
        call,
        localStream,
        onRemoteStream: (remote) => attach(remoteVideoRef, remote),
      });
      sessionRef.current = {
        ...handle, localStream, role: 'callee',
        watchUnsub: watchStatus(call.id),
      };
      setMode('active');
      attach(localVideoRef, localStream);
    } catch (e) {
      setNote(e?.message || 'Could not join — camera permission?');
      setTimeout(() => setNote(''), 4000);
      setCallStatus(call.id, 'ended');
      endSession(null);
    }
  };

  const decline = () => {
    if (incoming) setCallStatus(incoming.id, 'declined');
    stopRingBuzz();
    setIncoming(null);
    setMode(null);
  };

  const hangUp = () => endSession('ended');

  const toggleMic = () => {
    const s = sessionRef.current;
    if (!s?.localStream) return;
    const next = !micOn;
    s.localStream.getAudioTracks().forEach((t) => { t.enabled = next; });
    setMicOn(next);
  };
  const toggleCam = () => {
    const s = sessionRef.current;
    if (!s?.localStream) return;
    const next = !camOn;
    s.localStream.getVideoTracks().forEach((t) => { t.enabled = next; });
    setCamOn(next);
  };

  // Re-attach streams whenever the overlay (re)renders its videos.
  useEffect(() => {
    const s = sessionRef.current;
    if (s?.localStream) attach(localVideoRef, s.localStream);
  }, [mode]);

  useEffect(() => () => endSession('ended'), []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!mode && !note) return null;

  return (
    <>
      {note && !mode && (
        <div style={{
          position: 'fixed', top: 16, left: '50%', transform: 'translateX(-50%)', zIndex: 401,
          background: 'rgba(21,28,46,0.95)', border: '1px solid rgba(255,255,255,0.15)',
          borderRadius: 12, padding: '10px 18px', color: '#E8EAF0', fontSize: 16,
        }}>{note}</div>
      )}

      {mode && (
        <div style={overlayStyle}>
          {/* Remote video fills the stage (hidden until active). */}
          <div style={{ flex: 1, minHeight: 0, position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <video ref={remoteVideoRef} autoPlay playsInline
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: mode === 'active' ? 'block' : 'none' }} />
            {mode !== 'active' && (
              <div style={{ textAlign: 'center', padding: 24 }}>
                <div style={{
                  width: 92, height: 92, borderRadius: '50%', margin: '0 auto 18px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: 'linear-gradient(135deg,#E8007D,#7C3AED)',
                }}>
                  <Video size={40} />
                </div>
                <div style={{ fontFamily: '"Syne", sans-serif', fontWeight: 800, fontSize: 26 }}>{peerName}</div>
                <div style={{ fontSize: 17, color: '#8892A4', marginTop: 8 }}>
                  {mode === 'outgoing' ? 'Calling…' : 'Incoming video call'}
                </div>
                {note && <div style={{ fontSize: 16, color: '#f4b942', marginTop: 10 }}>{note}</div>}
              </div>
            )}
            {/* Local preview PiP — always live so you can frame yourself. */}
            <video ref={localVideoRef} autoPlay playsInline muted
              style={{
                position: 'absolute', right: 14, top: 14, width: 108, height: 144,
                objectFit: 'cover', borderRadius: 14, border: '1px solid rgba(255,255,255,0.2)',
                background: '#111827', transform: 'scaleX(-1)',
              }} />
          </div>

          {/* Controls */}
          <div style={{ display: 'flex', justifyContent: 'center', gap: 22, padding: '18px 16px 30px', flexShrink: 0 }}>
            {mode === 'incoming' ? (
              <>
                <button onClick={decline} aria-label="Decline" style={roundBtn('#DC2626')}><PhoneOff size={26} /></button>
                <button onClick={accept} aria-label="Accept" style={roundBtn('#16A34A')}><Phone size={26} /></button>
              </>
            ) : (
              <>
                <button onClick={toggleMic} aria-label="Toggle microphone"
                  style={roundBtn(micOn ? 'rgba(255,255,255,0.12)' : '#DC2626')}>
                  {micOn ? <Mic size={24} /> : <MicOff size={24} />}
                </button>
                <button onClick={hangUp} aria-label="Hang up" style={roundBtn('#DC2626')}><PhoneOff size={26} /></button>
                <button onClick={toggleCam} aria-label="Toggle camera"
                  style={roundBtn(camOn ? 'rgba(255,255,255,0.12)' : '#DC2626')}>
                  {camOn ? <Video size={24} /> : <VideoOff size={24} />}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default CallManager;
